import { useContext, useState, useMemo } from "react";
import { db, timestamp } from "../../firebase/config";
import { client } from "../../hooks/Client";
import { useFirestoreGeneralOrderBy } from "../../firebase/useFirestore";
import RandomColorGenerator from "../../hooks/RandomColorGenerator";
import buildNewOutput from "../../helpers/impactStrategy/buildNewOutput";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import TocDetailSection from "./TocDetailSection.jsx";

import {
  stakeholderTypes,
  stakeholderTypeLabels,
  primaryTargetGroupLabels,
  contextLabelLabels,
  contextLabels as allContextLabels,
  allowedPrimaryGroupsByStakeholderType,
  processTargetGroup,
} from "../../helpers/dataHelpers/targetgroupCategories";
import {
  activityTypes,
  activitySubtypesByType,
  activityTypeLabels,
  activitySubtypeLabels,
  deliveryModes,
  deliveryModeLabels,
  estimatedEffortTypes,
  estimatedEffortLabels,
  processActivity,
} from "../../helpers/dataHelpers/activityCategories";
import {
  mechanismCategoryLabels,
  getMechanismSignals,
  mechanismCategoriesForSector,
} from "../../helpers/dataHelpers/mechanismCategories";
import {
  measurementMethods,
  measurementMethodLabels,
  allowedMeasurementMethods,
  measurementMethodOf,
  registrationTypes,
  registrationTypeLabels,
  registrationTypeOf,
} from "../../helpers/dataHelpers/measurementMethods";

import SelectTargetgroup from "../activities/SelectTargetgroup";
import AdresFields from "../settings/AdresFields";
import SelectActivities from "../effects/SelectActivities";
import OutputIsSyncedTitle from "../syncronisations/OutputIsSyncedTitle";
import OutputIsSyncedDelete from "../syncronisations/OutputIsSyncedDelete";
import SelectMechanismActivities from "../mechanisms/SelectMechanismActivities.jsx";
import SelectMechanismEffects from "../mechanisms/SelectMechanismEffects.jsx";
import SelectLinkedMechanisms from "../mechanisms/SelectLinkedMechanisms.jsx";
import IndicatorsCount from "../effects/IndicatorsCount.jsx";
import AddIndicators from "../../pages/measurementPlan/AddIndicators";

/**
 * Focused editor for ONE chain item, rendered inline in the TOC navigator's
 * active card (F7). Shows only that node's fields — no table — with always-open
 * inline taxonomy and the existing link-selector components. Writes directly to
 * Firestore on change (auto-save), like the rest of the app.
 *
 * @param {object} props
 * @param {string} props.step - chain step id: prob|doel|act|mech|eff|imp
 * @param {object} props.item - the node's Firestore doc (carries docid)
 * @param {string[]} [props.sectors] - tenant sectors (CompagnyMeta.SelectedSectors),
 *   passed down rather than read here: this editor mounts and unmounts on every
 *   Bewerken toggle, and each useSettings() opens its own Firestore listeners.
 * @param {{ personasOn?: string, housevisits?: boolean }} [props.modules] - module
 *   flags from useSettings() that gate the output measurement methods (ADR-011);
 *   passed down for the same reason as `sectors`.
 */
const NodeEditor = ({ step, item, sectors, modules }) => {
  if (!item) return null;
  switch (step) {
    case "doel": return <StakeholderNodeEditor item={item} />;
    case "act": return <ActivityNodeEditor item={item} modules={modules} />;
    case "mech": return <MechanismNodeEditor item={item} sectors={sectors} />;
    case "eff": return <EffectNodeEditor item={item} />;
    default: return null;
  }
};

// ── shared bits ───────────────────────────────────────────────────────────────

/**
 * One editor row: label on the left, control on the right.
 *
 * @param {object} props
 * @param {string} props.label - field label
 * @param {React.ReactNode} props.children - the control(s)
 */
const Field = ({ label, children }) => (
  <div className="node-editor-field">
    <div className="node-editor-label">{label}</div>
    <div className="node-editor-control">{children}</div>
  </div>
);

/**
 * A dropdown for a long single-choice taxonomy list (12+ options read as a
 * wall of chips). Controlled `value`, never `defaultValue` — the options are
 * static but the doc arrives by snapshot (#ui-select-defaultvalue-loses-late-options).
 * A stored key outside `options` keeps its own option so the select shows the
 * truth instead of silently showing the placeholder.
 *
 * @param {object} props
 * @param {string} props.label - accessible name (the field label)
 * @param {string[]} props.options - taxonomy keys
 * @param {Record<string,string>} props.labels - key → display label
 * @param {string} props.value - stored key, '' when none
 * @param {(key: string) => void} props.onChange - called with the chosen key
 */
const TaxonomySelect = ({ label, options, labels, value, onChange }) => (
  <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
    <option value="">— Kies {label.toLowerCase()} —</option>
    {value !== "" && !options.includes(value) && <option value={value}>{labels[value] || value}</option>}
    {options.map((key) => (
      <option key={key} value={key}>{labels[key] || key}</option>
    ))}
  </select>
);

/**
 * @param {object} props
 * @param {string[]} props.options - taxonomy keys
 * @param {Record<string,string>} props.labels - key → display label
 * @param {(key: string) => boolean} props.isOn
 * @param {(key: string) => void} props.onToggle
 * @param {(key: string) => boolean} [props.dimWhen]
 */
const ChipRow = ({ options, labels, isOn, onToggle, dimWhen }) => (
  <div className="node-editor-chips">
    {options.map((key) => (
      <button
        key={key}
        type="button"
        className={`node-editor-chip${isOn(key) ? " is-on" : ""}`}
        onClick={() => onToggle(key)}
        style={dimWhen && dimWhen(key) ? { opacity: 0.4 } : undefined}
      >
        {labels[key] || key}
      </button>
    ))}
  </div>
);

const useSaved = () => {
  const [, setSaved] = useContext(SavedIcon);
  return () => setSaved("flex");
};

// ── Doelgroep ───────────────────────────────────────────────────────────────────

const StakeholderNodeEditor = ({ item }) => {
  const flag = useSaved();
  const type = item.stakeholderType || "";
  const groups = Array.isArray(item.primaryTargetGroups) ? item.primaryTargetGroups : [];
  const labels = Array.isArray(item.contextLabels) ? item.contextLabels : [];
  const allowedGroups = allowedPrimaryGroupsByStakeholderType[type] || [];

  const writeTaxonomy = (next) => {
    const processed = processTargetGroup(next);
    db.collection("Stakeholders").doc(item.docid).update({
      stakeholderType: processed.stakeholderType,
      // Both shapes: the UI reads the array, benchmark/portfolio-sync read the
      // single primary group (ADR-009 — first selected).
      primaryTargetGroup: processed.primaryTargetGroup,
      primaryTargetGroups: processed.primaryTargetGroups,
      contextLabels: processed.contextLabels,
      analysisTags: processed.analysisTags,
      impactMapping: processed.impactMapping,
    }).then(flag);
  };

  const onName = (e) => db.collection("Stakeholders").doc(item.docid).update({ Organisation: e.target.value }).then(flag);
  const onDescription = (e) => db.collection("Stakeholders").doc(item.docid).update({ Description: e.target.value }).then(flag);
  const pickType = (t) => writeTaxonomy({ stakeholderType: t, primaryTargetGroups: [], contextLabels: [] });
  const toggleGroup = (key) => {
    const next = groups.includes(key) ? groups.filter((k) => k !== key) : groups.length >= 3 ? groups : [...groups, key];
    writeTaxonomy({ stakeholderType: type, primaryTargetGroups: next, contextLabels: labels });
  };
  const toggleLabel = (key) => {
    const next = labels.includes(key) ? labels.filter((k) => k !== key) : labels.length >= 5 ? labels : [...labels, key];
    writeTaxonomy({ stakeholderType: type, primaryTargetGroups: groups, contextLabels: next });
  };

  return (
    <div className="node-editor">
      <Field label="Naam">
        <input type="text" defaultValue={item.Organisation || ""} placeholder="Omschrijving van doelgroep" onChange={onName} />
      </Field>
      <Field label="Beschrijving">
        <textarea className="node-editor-textarea" rows="3" defaultValue={item.Description || ""} placeholder="Beschrijving" onChange={onDescription} />
      </Field>
      <TocDetailSection className="node-editor-block" label="Kenmerken">
        <Field label="Type">
          <ChipRow options={stakeholderTypes} labels={stakeholderTypeLabels} isOn={(k) => type === k} onToggle={pickType} />
        </Field>
        {allowedGroups.length > 0 && (
          <Field label={`Primaire doelgroep (${groups.length}/3)`}>
            <ChipRow options={allowedGroups} labels={primaryTargetGroupLabels} isOn={(k) => groups.includes(k)} onToggle={toggleGroup} dimWhen={(k) => !groups.includes(k) && groups.length >= 3} />
          </Field>
        )}
        <Field label={`Contextkenmerken (${labels.length}/5)`}>
          <ChipRow options={allContextLabels} labels={contextLabelLabels} isOn={(k) => labels.includes(k)} onToggle={toggleLabel} dimWhen={(k) => !labels.includes(k) && labels.length >= 5} />
        </Field>
      </TocDetailSection>
    </div>
  );
};

// ── Activiteit ──────────────────────────────────────────────────────────────────

/**
 * Measurement methods to offer for one output: the module-gated set, plus the
 * stored method when its module is off, so the editor shows what is saved.
 *
 * @param {string[]} allowed - allowedMeasurementMethods(modules)
 * @param {object} output - the Outputs doc
 * @returns {string[]} in canonical order
 */
const methodOptionsFor = (allowed, output) => {
  const options = new Set([...allowed, measurementMethodOf(output)]);
  return measurementMethods.filter((method) => options.has(method));
};

const ActivityNodeEditor = ({ item, modules }) => {
  const flag = useSaved();
  // Outputs whose (empty) description field the user opened this session.
  const [openDescriptions, setOpenDescriptions] = useState(/** @type {string[]} */ ([]));
  const openDescription = (id) => setOpenDescriptions((ids) => (ids.includes(id) ? ids : [...ids, id]));
  // The output whose description was just opened with the button: its field
  // takes focus, so one click is enough and keyboard focus doesn't drop to body.
  const [focusDescription, setFocusDescription] = useState(/** @type {string | null} */ (null));
  const subtypes = activitySubtypesByType[item.activityType] || [];

  // Outputs belong to the activity (linked by ActivityID = the activity's ID).
  // Edited here inline; the dedicated Outputs page still owns reorder + doel.
  const outputsRaw = useFirestoreGeneralOrderBy("Outputs", "ActivityID", item.ID || "", "Position", "asc");
  const outputs = useMemo(() => (outputsRaw || []).filter((o) => !o.Deleted), [outputsRaw]);
  const randomColor = RandomColorGenerator();

  const addOutput = () => {
    db.collection("Outputs")
      .doc()
      .set(buildNewOutput({ client, activityID: item.ID, color: randomColor, position: (outputs?.length || 0) + 1, timestamp }))
      .then(flag);
  };
  const onOutputDescription = (e) => {
    const docid = e.target.dataset.docid;
    db.collection("Outputs").doc(docid).update({ Description: e.target.value }).then(flag);
  };

  // AI-CONTEXT: See docs/features/output-registratie-per-keer-of-totaal.md (ADR-070)
  // Each chip writes only its own field (#state-handler-overwrites-sibling-field):
  // switching the method away from the form leaves RegistrationType in place,
  // where registrationTypeOf() ignores it.
  const allowedMethods = allowedMeasurementMethods(modules);
  const writeOutputField = (docid, patch) =>
    db.collection("Outputs").doc(docid).update(patch).then(flag)
      .catch((error) => console.error("Output kon niet worden opgeslagen", patch, error));

  const writeTaxonomy = (patch) => {
    const next = {
      activityName: item.Activity,
      activityType: item.activityType || "",
      activitySubtype: item.activitySubtype || "",
      deliveryMode: item.deliveryMode || "",
      estimatedEffort: item.estimatedEffort || "",
      ...patch,
    };
    const processed = processActivity(next);
    db.collection("Activities").doc(item.docid).update({
      activityType: processed.activityType,
      activitySubtype: processed.activitySubtype,
      deliveryMode: processed.deliveryMode,
      estimatedEffort: processed.estimatedEffort,
    }).then(flag);
  };

  const onName = (e) => db.collection("Activities").doc(item.docid).update({ Activity: e.target.value }).then(flag);
  const onDescription = (e) => db.collection("Activities").doc(item.docid).update({ Description: e.target.value }).then(flag);

  return (
    <div className="node-editor">
      <Field label="Naam">
        <input type="text" defaultValue={item.Activity || ""} placeholder="Titel" onChange={onName} />
      </Field>
      <Field label="Beschrijving">
        <textarea className="node-editor-textarea" rows="3" defaultValue={item.Description || ""} placeholder="Beschrijving" onChange={onDescription} />
      </Field>
      <TocDetailSection className="node-editor-block" label="Kenmerken">
        <Field label="Type">
          <TaxonomySelect label="Type" options={activityTypes} labels={activityTypeLabels} value={item.activityType || ""} onChange={(k) => writeTaxonomy({ activityType: k, activitySubtype: "" })} />
        </Field>
        {subtypes.length > 0 && (
          <Field label="Subtype">
            <TaxonomySelect label="Subtype" options={subtypes} labels={activitySubtypeLabels} value={item.activitySubtype || ""} onChange={(k) => writeTaxonomy({ activitySubtype: k })} />
          </Field>
        )}
        <Field label="Uitvoering">
          <ChipRow options={deliveryModes} labels={deliveryModeLabels} isOn={(k) => item.deliveryMode === k} onToggle={(k) => writeTaxonomy({ deliveryMode: k })} />
        </Field>
        <Field label="Intensiteit">
          <ChipRow options={estimatedEffortTypes} labels={estimatedEffortLabels} isOn={(k) => item.estimatedEffort === k} onToggle={(k) => writeTaxonomy({ estimatedEffort: k })} />
        </Field>
      </TocDetailSection>
      <TocDetailSection className="node-editor-block" label="Koppelingen">
        <Field label="Doelgroepen">
          <SelectTargetgroup activity={item} />
        </Field>
        <Field label="Mechanismen">
          <SelectLinkedMechanisms targetId={item.ID} field="Activity" />
        </Field>
        <Field label="Locatie">
          <AdresFields comp={item} collection="Activities" bare />
        </Field>
      </TocDetailSection>
      <TocDetailSection className="node-editor-block" label="Outputs">
        <div className="node-editor-outputs">
          {(outputs || []).map((output) => (
            <div className="node-editor-output" key={output.ID}>
              <div className="node-editor-output-head">
                <OutputIsSyncedTitle outputID={output.ID} />
                <OutputIsSyncedDelete outputID={output.ID} />
              </div>
              <div className="node-editor-output-settings">
                <div className="node-editor-output-setting">
                  <span className="node-editor-output-setting-label">Meetmethode</span>
                  <ChipRow
                    options={methodOptionsFor(allowedMethods, output)}
                    labels={measurementMethodLabels}
                    isOn={(k) => measurementMethodOf(output) === k}
                    onToggle={(k) => writeOutputField(output.docid, { MeasurementMethod: k })}
                  />
                </div>
                {measurementMethodOf(output) === "event_form" && (
                  <div className="node-editor-output-setting">
                    <span className="node-editor-output-setting-label">Registratie</span>
                    <ChipRow
                      options={registrationTypes}
                      labels={registrationTypeLabels}
                      isOn={(k) => registrationTypeOf(output) === k}
                      onToggle={(k) => writeOutputField(output.docid, { RegistrationType: k })}
                    />
                  </div>
                )}
              </div>
              {/* An empty description stays out of the way until asked for. */}
              {/* onFocus keeps it open: clearing the text must not swap the
                  field for the button under the user's cursor. */}
              {output.Description || openDescriptions.includes(output.ID) ? (
                <textarea
                  className="node-editor-textarea"
                  rows="2"
                  data-docid={output.docid}
                  defaultValue={output.Description || ""}
                  placeholder="Beschrijving van de output"
                  onChange={onOutputDescription}
                  onFocus={() => openDescription(output.ID)}
                  autoFocus={focusDescription === output.ID}
                />
              ) : (
                <button
                  type="button"
                  className="node-editor-link-btn node-editor-output-adddesc"
                  onClick={() => { openDescription(output.ID); setFocusDescription(output.ID); }}
                >
                  + Beschrijving toevoegen
                </button>
              )}
            </div>
          ))}
          <button type="button" className="node-editor-add-output" onClick={addOutput}>
            + Output toevoegen
          </button>
        </div>
      </TocDetailSection>
    </div>
  );
};

// ── Werkzaam mechanisme ─────────────────────────────────────────────────────────

const MechanismNodeEditor = ({ item, sectors }) => {
  const flag = useSaved();
  const update = (patch) => db.collection("TocMechanisms").doc(item.docid).update(patch).then(flag);
  // Concrete mechanisms come from the category's signal vocabulary (no free
  // typing) so they stay comparable across tenants. A legacy/free-text Title is
  // kept as a fallback option rather than silently dropped.
  const signals = getMechanismSignals(item.Category);
  const title = item.Title || "";
  const hasLegacyTitle = title !== "" && !signals.includes(title);
  // Authoring surface → narrows to the tenant's sector (ADR-031). A Category
  // outside that set keeps its own option, or the select renders blank and one
  // change event wipes a stored value.
  const sectorCategories = mechanismCategoriesForSector(sectors);
  const category = item.Category || "";
  const hasOutOfSectorCategory = category !== "" && !sectorCategories.includes(category);
  return (
    <div className="node-editor">
      <Field label="Categorie">
        <select value={category} onChange={(e) => update({ Category: e.target.value })}>
          <option value="">— Kies categorie —</option>
          {hasOutOfSectorCategory && (
            <option value={category}>{mechanismCategoryLabels[category] || category}</option>
          )}
          {sectorCategories.map((key) => (
            <option key={key} value={key}>{mechanismCategoryLabels[key]}</option>
          ))}
        </select>
      </Field>
      <Field label="Mechanisme">
        <select value={title} onChange={(e) => update({ Title: e.target.value })} disabled={!item.Category}>
          <option value="">— Kies mechanisme —</option>
          {hasLegacyTitle && <option value={title}>{title}</option>}
          {signals.map((signal) => (
            <option key={signal} value={signal}>{signal}</option>
          ))}
        </select>
      </Field>
      <Field label="Beschrijving">
        <textarea className="node-editor-textarea" rows="3" defaultValue={item.Description || ""} placeholder="Beschrijving" onChange={(e) => update({ Description: e.target.value })} />
      </Field>
      <TocDetailSection className="node-editor-block" label="Koppelingen">
        <Field label="Activiteiten">
          <SelectMechanismActivities mechanism={item} />
        </Field>
        <Field label="Effecten">
          <SelectMechanismEffects mechanism={item} />
        </Field>
      </TocDetailSection>
    </div>
  );
};

// ── Effect ──────────────────────────────────────────────────────────────────────

const EffectNodeEditor = ({ item }) => {
  const flag = useSaved();
  const [showIndicators, setShowIndicators] = useState(false);

  const update = (patch) => db.collection("OutputEffects").doc(item.docid).update(patch).then(flag);

  // MSI (Meetstandaard) effects have a fixed, standardised description — read-only.
  const isMsi = !!item.MSIId;

  return (
    <div className="node-editor">
      <Field label="Effect">
        <input
          type="text"
          defaultValue={item.Effect || ""}
          placeholder="Effect"
          readOnly={isMsi}
          onChange={isMsi ? undefined : (e) => update({ Effect: e.target.value })}
        />
        {isMsi && <span className="node-editor-hint">Overgenomen uit Meetstandaard — niet aanpasbaar.</span>}
      </Field>
      <Field label="Beschrijving">
        <textarea
          className="node-editor-textarea"
          rows="3"
          defaultValue={item.Description || ""}
          placeholder="Beschrijving"
          readOnly={isMsi}
          onChange={isMsi ? undefined : (e) => update({ Description: e.target.value })}
        />
        {isMsi && <span className="node-editor-hint">Overgenomen uit Meetstandaard — niet aanpasbaar.</span>}
      </Field>
      <TocDetailSection className="node-editor-block" label="Koppelingen">
        <Field label="Activiteiten">
          <SelectActivities effect={item} />
        </Field>
        <Field label="Mechanismen">
          <SelectLinkedMechanisms targetId={item.ID} field="Effect" />
        </Field>
      </TocDetailSection>
      <TocDetailSection className="node-editor-block" label="Indicatoren">
        <div className="node-editor-indicators">
          <span className="node-editor-indicator-count"><IndicatorsCount effectId={item.ID} /> indicator(en)</span>
          <button type="button" className="node-editor-link-btn" onClick={() => setShowIndicators((v) => !v)}>
            {showIndicators ? "Verbergen" : "Indicatoren beheren"}
          </button>
        </div>
        {showIndicators && (
          <div className="node-editor-indicators-inline">
            <AddIndicators effectId={item.ID} showHeader={false} />
          </div>
        )}
      </TocDetailSection>
    </div>
  );
};

export default NodeEditor;
